import {
  useDocumentActive,
  useInterval,
  useLatestFn,
  useMount,
  useShallowMemo,
  useUnmount,
  useUpdateEffect
} from '@studio/react-utils/hooks';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useRef, useState } from 'react';
import { Continuous, PrefKeys, Recall } from './constants.js';
import type { Action, Collection, Profile, Profiles } from './types.js';
import { createProfile, deserialize, evaluate, getGamepad, serialize } from './utils.js';
import { markSessionReplayGamepadActivity } from './activity.js';
import { useNotifier } from '#~/command/hooks.js';
import { Command } from '#~/command/index.js';
import { logger } from '#~/logger/index.js';
import { usePrefState, useProducer } from '#~/preferences/hooks.js';
import type { UsePrefState } from '#~/preferences/hooks.js';
import { useSelectedVideo } from '#~/room/hooks.js';
import { useRatePref } from '#~/control/movement/hooks.js';
import { Direction, Facet } from '#~/control/movement/index.js';

/**
 * Hook to keep track of the connected gamepads.
 *
 * @returns The connected gamepad IDs.
 */
export const useGamepadIds = (): Collection => {
  const [gamepadIds, setGamepadIds] = useState(() =>
    navigator
      .getGamepads()
      .filter((gamepad): gamepad is Gamepad => !!gamepad)
      .map((gamepad) => gamepad.id)
  );

  useMount(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    window.addEventListener(
      'gamepadconnected',
      ({ gamepad }) => {
        setGamepadIds((previousGamepadIds) => {
          const ids = new Set(previousGamepadIds);

          ids.add(gamepad.id);

          return [...ids.values()];
        });
      },
      { signal }
    );

    window.addEventListener(
      'gamepaddisconnected',
      ({ gamepad }) => {
        setGamepadIds((previousGamepadIds) => {
          const ids = new Set(previousGamepadIds);

          ids.delete(gamepad.id);

          return [...ids.values()];
        });
      },
      { signal }
    );

    return () => {
      abortController.abort();
    };
  });

  return gamepadIds;
};

/**
 * Mange whether we should show the user the Gamepad hint.
 *
 * @returns Gamepad hint state.
 */
export const useHintPref = (): UsePrefState<boolean> => usePrefState(PrefKeys.hint, true);

/** Registers the observer that watches for gamepad input. */
export const useObserver = (): void => {
  const gamepadIds = useGamepadIds();
  const notifyFn = useNotifier();
  const profiles = useProfiles(gamepadIds);
  const isDocumentActive = useDocumentActive();
  const recall = useRef(new Map<Gamepad['id'], T_Maybe<Set<Action>>>()).current;
  const updateSpeed = useUpdateSpeed();

  const gamepadCount = gamepadIds.length;
  const isIntervalActive = gamepadCount > 0 && isDocumentActive;

  const handleCleanup = useLatestFn((): void => {
    const { focus, movements } = Recall;

    recall.forEach((previous) => {
      if (!previous?.size) {
        return;
      }

      if (movements.some((movementCommand) => previous.has(movementCommand))) {
        notifyFn({ command: Command.PTZ_STOP });
      }

      if (focus.some((focusCommand) => previous.has(focusCommand))) {
        notifyFn({ command: Command.FOCUS_STOP });
      }
    });

    recall.clear();
  });

  useUnmount(handleCleanup);

  const interval = useInterval(
    () => {
      /* Thing fast. Be fast. */

      // Bail early if there are no gamepads.
      if (gamepadCount === 0) {
        return;
      }

      // Process each connected gamepad.
      for (let ii = 0; ii < gamepadCount; ii++) {
        const gamepadId = gamepadIds[ii];

        if (!gamepadId) {
          continue;
        }

        const gamepad = getGamepad(gamepadId);

        if (!gamepad) {
          continue;
        }

        const profile = profiles[gamepadId];

        if (!profile) {
          continue;
        }

        // Run the event through the gamepad processor.
        const directives = evaluate(gamepad, profile.formula);
        const directiveCount = directives?.length ?? 0;

        if (directiveCount) {
          markSessionReplayGamepadActivity();
        }

        // Get the previous set of actions.
        const previous = recall.get(gamepad.id);

        // Best case, bail early.
        if (!previous && !directiveCount) {
          continue;
        }

        // Run through all of the directives. (if applicable)
        for (let jj = 0; jj < directiveCount; jj++) {
          const directive = directives?.[jj];

          if (!directive) {
            continue;
          }

          const { command } = directive;

          // A momentary action is only triggered once per press.
          if (!Continuous.has(command) && previous?.has(command)) {
            continue;
          }
          // Notify listeners.
          notifyFn(directive);

          // Special speed increase/decrease handling.
          if (directive.command === Command.PT_SPEED_UP) {
            updateSpeed(Direction.Up);
          }
          if (directive.command === Command.PT_SPEED_DOWN) {
            updateSpeed(Direction.Down);
          }
        }

        // For camera movement and focus, we may need to send stop commands.
        const { focus, movements } = Recall;
        const current = new Set((directives ?? []).map((directive) => directive.command));

        if (
          !movements.some((movementCommand) => current.has(movementCommand)) &&
          movements.some((movementCommand) => previous?.has(movementCommand))
        ) {
          notifyFn({ command: Command.PTZ_STOP });
        }

        if (
          !focus.some((focusCommand) => current.has(focusCommand)) &&
          focus.some((focusCommand) => previous?.has(focusCommand))
        ) {
          notifyFn({ command: Command.FOCUS_STOP });
        }

        // Update previous set of actions.
        recall.set(gamepad.id, current.size ? current : undefined);
      }
    },
    { period: 100, start: isIntervalActive }
  );

  useUpdateEffect(() => {
    logger.info(`${isIntervalActive ? 'Starting' : 'Stopping'} gamepad listener.`);

    interval[isIntervalActive ? 'start' : 'stop']();

    // If the interval stops, clean up after thyself.
    if (!isIntervalActive) {
      handleCleanup();
    }
  }, [isIntervalActive]);
};

/**
 * Returns the profile accessor for the specified gamepad.
 *
 * @param gamepadId - Gamepad ID.
 * @returns Profile accessor.
 */
export const useProfile = (gamepadId: Gamepad['id']): [Profile, (profile: Profile) => void] => {
  const profiles = useProfiles(useGamepadIds());
  const producer = useProducer();

  // Get the profile for the specified gamepad.
  const profile = profiles[gamepadId] ?? createProfile(gamepadId);

  // Updated handler.
  const handleUpdate = useLatestFn((nextProfile: Profile) => {
    producer((draft) => {
      draft.setValue(
        PrefKeys.profiles,
        serialize({
          ...profiles,
          [gamepadId]: nextProfile
        })
      );
    });
  });

  return [profile, handleUpdate];
};

/**
 * Returns the gamepad profiles.
 *
 * @param gamepadIds - The gamepad IDs.
 * @returns The gamepad profiles.
 */
export function useProfiles(gamepadIds: Collection): Profiles {
  const [state] = usePrefState(PrefKeys.profiles, '{}');

  return useShallowMemo((): Profiles => {
    // Deserialize the known profiles.
    const knownProfiles = deserialize(state);

    // Make sure we have a profile for each gamepad.
    const profiles: Profiles = {};

    gamepadIds.forEach((gamepadId) => {
      // Do we know about this profile?
      const profile = knownProfiles[gamepadId];

      if (profile) {
        profiles[gamepadId] = profile;

        return;
      }

      // We don't know about this gamepad, so create a new profile.
      profiles[gamepadId] = createProfile(gamepadId);
    });

    return profiles;
  }, [gamepadIds, state]);
}

/**
 * Mange gamepad zoom lock.
 *
 * @returns Gamepad zoom lock state.
 */
export function useZoomLockPref(): UsePrefState<boolean> {
  return usePrefState(PrefKeys.lock, false);
}

function useUpdateSpeed(): (direction: Direction) => void {
  const [selectedVideo] = useSelectedVideo();
  const [panTiltRate, setPanTiltRate] = useRatePref(selectedVideo!, Facet.PanTilt);

  return (direction: Direction) => {
    if (!selectedVideo) return;

    const increasePercentage = 0.05;
    let newSpeed = panTiltRate.value;

    if (direction === Direction.Up) {
      newSpeed = Math.min(panTiltRate.value + increasePercentage, 1);
    } else if (direction === Direction.Down) {
      newSpeed = Math.max(panTiltRate.value - increasePercentage, 0);
    }
    const newRate = { ...panTiltRate, value: newSpeed };
    setPanTiltRate(newRate);
  };
}
